import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  addDays,
  isSameMonth,
  isSameDay,
  format
} from 'date-fns';
import { it } from 'date-fns/locale';

// ============================================
// TYPES
// ============================================

interface MiniCalendarProps {
  currentDate: Date;
  onDateSelect: (date: Date) => void;
  onMonthChange?: (date: Date) => void;
}

// ============================================
// COMPONENT
// ============================================

export function MiniCalendar({
  currentDate,
  onDateSelect,
  onMonthChange
}: MiniCalendarProps) {
  // State per il mese visualizzato nel mini calendario (può essere diverso dal currentDate)
  const [viewMonth, setViewMonth] = useState(currentDate);

  // Giorni della settimana in italiano, dal lunedì come il resto del calendario
  const weekDays = ['Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa', 'Do'];

  // Calcola tutti i giorni da mostrare nella griglia
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(viewMonth);
    const monthEnd = endOfMonth(viewMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Lunedì
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days: Date[] = [];
    let day = calendarStart;

    while (day <= calendarEnd) {
      days.push(day);
      day = addDays(day, 1);
    }

    return days;
  }, [viewMonth]);

  // Handlers navigazione
  const handlePrevMonth = () => {
    const newMonth = subMonths(viewMonth, 1);
    setViewMonth(newMonth);
    onMonthChange?.(newMonth);
  };

  const handleNextMonth = () => {
    const newMonth = addMonths(viewMonth, 1);
    setViewMonth(newMonth);
    onMonthChange?.(newMonth);
  };

  // Oggi
  const today = new Date();

  return (
    <div className="select-none">
      {/* Header: < January 2024 > */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={handlePrevMonth}
          className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
        >
          <ChevronLeft className="w-4 h-4 text-gray-400" />
        </button>

        <span className="text-sm font-semibold text-black/90 dark:text-white/90">
          {format(viewMonth, 'MMMM yyyy', { locale: it })}
        </span>

        <button
          onClick={handleNextMonth}
          className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
        >
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* Grid Header: Su Mo Tu We Th Fr Sa */}
      <div className="grid grid-cols-7 mb-1">
        {weekDays.map((day) => (
          <div
            key={day}
            className="h-7 flex items-center justify-center text-[10px] font-medium text-gray-400 uppercase"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Grid Body: 6 righe x 7 colonne */}
      <div className="grid grid-cols-7">
        {calendarDays.map((day, index) => {
          const isToday = isSameDay(day, today);
          const isSelected = isSameDay(day, currentDate) && !isToday;
          const isCurrentMonth = isSameMonth(day, viewMonth);

          return (
            <button
              key={index}
              onClick={() => onDateSelect(day)}
              className={`
                h-7 w-7 mx-auto flex items-center justify-center text-xs rounded-full
                transition-colors
                ${isToday
                  ? 'bg-red-500 text-white font-medium'
                  : isSelected
                    ? 'bg-black/10 dark:bg-white/10 text-black/90 dark:text-white/90'
                    : isCurrentMonth
                      ? 'text-black/80 dark:text-white/80 hover:bg-black/5 dark:hover:bg-white/5'
                      : 'text-gray-300 dark:text-gray-600 hover:bg-black/5 dark:hover:bg-white/5'
                }
              `}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>
    </div>
  );
}
